<template>
    <div :class="{ 
        'mobile': mobile === true,
        'golf-reservation': calendarType === CALENDAR_TYPE.golf,
        'modern-reservation-calendar': useModernReservationCalendar,
    }">
        <header-notifications />
        <template v-if="viewState === VIEW_STATES.ready">
            <GolfMain 
                v-if="calendarType === CALENDAR_TYPE.golf"
                @adminToggle="onAdminModeToggle" />
            <CalendarMain 
                v-else-if="[CALENDAR_TYPE.resource, CALENDAR_TYPE.weekCalendar, CALENDAR_TYPE.grid].includes(calendarType)"
                @adminToggle="onAdminModeToggle" />
            <EquipmentMain 
                v-else-if="calendarType === CALENDAR_TYPE.resourceEquipment"
                @adminToggle="onAdminModeToggle" />
            <div class="row">
                <div class="col-xs-12" v-if="cartIsPopulated">
                    <button class="btn btn-oldstyle-info pull-right" v-if="isEmbed" @click="redirectToCart">{{ $_locale('Proceed to checkout') }}</button>
                    <a class="btn btn-oldstyle-info pull-right" v-else :href="checkoutUrl">{{ $_locale('Proceed to checkout') }}</a>
                </div>
            </div>
        </template>
        <template v-else-if="viewState === VIEW_STATES.loading">
            <calendar-placeholder />
        </template>
        <template v-else-if="viewState === VIEW_STATES.productNotAvailable">
            <div>{{ $_locale('%s is not available', true, $_locale('product')) }}</div>
        </template>
        
        <wn-modal ref="commonModal" :modalClass="modalClass"
            @close="$store.commit('common/setModal', null)">
            <div id="modal-portal-target"></div>
        </wn-modal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CalendarPlaceholder from './common/CalendarPlaceholder.vue'
import HeaderNotifications from './common/HeaderNotifications.vue'
import { on, trigger } from '../common/events.js'

const VIEW_STATES = {
    idle: 0,
    loading: 1,
    ready: 2,
    productNotAvailable: -1,
}

export default {
    components: {
        CalendarPlaceholder,
        HeaderNotifications
    },
    data: () => ({
        VIEW_STATES,
        viewState: VIEW_STATES.idle,
        embedModalPositionY: 0,
        useModernReservationCalendar: false,
    }),
    computed: {
        ...mapGetters({
            modalName: 'common/modalName',
            modalIsOpen: 'common/modalIsOpen',
            modalClass: 'common/modalClass',
            mobile: 'common/mobile',
            calendarType: 'reservations/calendarType',
            CALENDAR_TYPE: 'reservations/CALENDAR_TYPE',
            date: 'reservations/date',
            dateChangeInProgress: 'reservations/dateChangeInProgress',
            checkoutUrl: 'common/checkoutUrl',
            cartIsPopulated: 'ecom/cartIsPopulated',
            isEmbed: 'common/isEmbed',
            adminMode: 'common/adminMode',
        }),
        skipLoop() {
            return this.modalIsOpen || this.dateChangeInProgress;
        }
    },
    watch: {
        modalIsOpen(val, oldVal) {
            const commonModal = this.$refs.commonModal;
            
            if (val) {
                commonModal.open();
                
                if (this.isEmbed) {
                    this.$nextTick(() => {
                        commonModal.$el.querySelector('.modal-content').style.top = `${ this.embedModalPositionY }px`;
                    })
                }
            } else {
                commonModal.close();
            }
        },
        adminMode(val) {
            const func = val ? 'add' : 'remove'

            document.querySelector('.calendar-table-info .reservation-table').classList[func]('admin-mode')
        },
    },
    methods: {
        onAdminModeToggle() {
            if (this.isEmbed) {
                this.$nextTick(() => this.refreshParentHeight())
            }

            trigger(document.body, 'WnCartSetAdminMode', { value: this.adminMode })
        },
        redirectToCart() {
            const postData = {
                type: 'spacheckout',
                url: this.checkoutUrl
            }
            
            window.top.postMessage(JSON.stringify(postData), '*');
        },
        refreshParentHeight() {
            const { product } = window.productData
            
            setTimeout(() => {
                const height = document.querySelector('#content-background').getBoundingClientRect().height
                const postData = {
                    type: 'windowresize',
                    height,
                    productId: product.productId,
                }
                window.top.postMessage(JSON.stringify(postData), '*')
            }, 300)          
        }
    },
    mounted() {
        const { commit, dispatch } = this.$store;
        const { product } = window.productData;
        const refreshRate = 10000;
        let lastRefresh = 0;
        
        window.addEventListener('resize', () => {
			const isMobile = window.innerWidth <= 640;
			
			this.$store.commit('common/setMobile', isMobile)
		}, { passive: true })

        if (this.isEmbed) {
            const styleSheet = document.createElement('style');

            document.body.appendChild(styleSheet);

            const sheet = styleSheet.sheet;

            sheet.insertRule(`.sweet-alert {
                top: 200px !important;
            }`, 0);

            window.addEventListener('message', (e) => {
                try {
                    const data = JSON.parse(e.data);

                    if (typeof data === 'object' && data !== null) {
                        if (data.type === 'parentscroll') {
                            const { scrollTop, frameTop } = data;
                            
                            if (scrollTop - frameTop > 0) {
                                this.embedModalPositionY = scrollTop - frameTop;
                            } else {
                                this.embedModalPositionY = 0;
                            }

                            if (sheet.rules.length > 0) {
                                sheet.deleteRule(0);
                            }

                            sheet.insertRule(`.sweet-alert {
                                top: calc(200px + ${ this.embedModalPositionY }px) !important;
                            }`, 0);
                        }
                    }
                } catch(e) {

                }  
            })
        }
        
        const refreshLoop = (firstRun = false) => {
            requestAnimationFrame(() => refreshLoop())
            
            if (Date.now() - lastRefresh < refreshRate) {
                return;
            }

            if (firstRun || this.skipLoop) {
                return;
            }
            
            lastRefresh = Date.now();

            dispatch('reservations/getReservations', { date: this.date })
            .then(() => {})
        }

        this.viewState = VIEW_STATES.loading;        
        this.$_http({
            method: 'GET',
            url: `${ window.wisenetwork.REST_URL }/reservations/initialization/?productid=${ product.productId }`,
            withCredentials: true,
        })
        .then(({ data }) => {
            // bit of a hack
            if (data.ADMIN_URL) {
                window.wisenetwork.ADMIN_URL = data.ADMIN_URL
            }

            if (data.useModernReservationCalendar) {
                this.useModernReservationCalendar = true
                document.querySelector('.calendar-table-info').classList.add('modern-reservation-calendar')
                
                if (!this.adminMode) {
                    document.querySelector('.calendar-table-info .reservation-table').classList.remove('admin-mode')
                }
            }
        })
                
        dispatch('ecom/getProductInfo', product.productId)
        .then((response) => {
            const { data } = response;
            const date = this.date;
            
            if (data.rows.length === 0) {
                this.viewState = VIEW_STATES.productNotAvailable;
            }
            
            dispatch('reservations/getReservationSettings', { date, firstLoad: true })
            .then(async (result) => {
                // not an ajax xhr object!
                const { date } = result;

                dispatch('memberships/init', {});
                await dispatch('reservations/init');
                await dispatch('reservations/getReservations', { date, force: true })

                refreshLoop(true)
                        
                if (this.isEmbed) {
                    this.$nextTick(() => this.refreshParentHeight())
                }

                this.viewState = VIEW_STATES.ready;
            })
        })
        
        dispatch('ecom/getMyCart')
        .then((response) => {
            const data = response.data;
            const reservations = data.rows.filter((row) => row.type === 6);

            if (reservations.length > 0) {
                if (!!data.orderId) {
                    commit('reservations/setDisabled', true)
                }

                this.$_swal({
                    title: _locale('unconfirmed reservation in the shopping cart').ucfirst(),
                    text: '',
                    type: 'info',
                    showCancelButton: true,
                    cancelButtonClass: !!data.orderId ? 'btn-danger' : 'btn-default',
                    confirmButtonClass: 'btn-success',
                    confirmButtonText: _locale('Proceed to checkout'),
                    cancelButtonText: _locale(!!data.orderId ? 'cancel reservation' : 'Continue shopping').ucfirst(),
                })
                .then((result) => {
                    if (result.isConfirmed) {
                        if (this.isEmbed) {
                            this.redirectToCart();
                        } else {
                            location.href = this.checkoutUrl;
                        }
                    } else {
                        if (!!data.orderId) {
                            dispatch('ecom/deleteCartOrder')
                            .then(() => {

                            })
                            .catch((error) => {
                                this.$_swal({ icon: 'error', title: error })
                            }).finally(() => {
                                location.reload();
                            })
                        }
                    }
                })
            }

            trigger(document.body, 'WnCartSetAdminMode', { value: this.adminMode })
        })

        on(
            document.body,
            'WnCartUpdate', 
            (evt, { rows }) => {
                this.$store.commit('ecom/setCart', rows)
            }
        )
    }
}
</script>